import { CalculatedValue } from "../../models/text/CalculatedValue";
import { LayoutLine, ProcessedWord } from "./SplitMeasuredTokens";
import { ISplitOptions } from "../../models/text/ISplitOptions";
import { SPLIT_ELEMENT_CLASS } from "../../models/text/SplitElementClass";
import { FontMetrics } from "./CanvasKerningApplier";
import { WrapperInfo } from "./BuildTokens";

function getWholeWordSplitClass(word: any): string[] {
  if (!word || !Array.isArray(word.chars) || word.chars.length === 0) return [];
  // Ignore neutral chars (no explicit splitClass). Only require explicit ones to agree.
  let first: string[] | null = null;
  for (const ch of word.chars) {
    const cls: string[] = (ch as any).splitClass ?? [];
    if (cls.length === 0) continue;
    if (first === null) { first = cls; continue; }
    if (cls.length !== first.length) return [];
    for (let i = 0; i < cls.length; i++) {
      if (cls[i] !== first[i]) return [];
    }
  }
  return first ?? [];
}

function getWordWrappers(word: ProcessedWord): WrapperInfo[] {
  return (word.chars[0]?.token?.meta?.wrappers as WrapperInfo[]) ?? [];
}

function shouldTrimGeneratedGap(
  currentWord: ProcessedWord,
  nextWord: ProcessedWord | undefined,
  options: ISplitOptions,
): boolean {
  if (options.trimInlineGaps !== true || !nextWord) return false;

  const currentWrappers = getWordWrappers(currentWord);
  const nextWrappers = getWordWrappers(nextWord);
  if (currentWrappers.length === 0 || nextWrappers.length === 0) return false;
  if (currentWrappers.length !== nextWrappers.length) return true;

  for (let i = 0; i < currentWrappers.length; i++) {
    if (currentWrappers[i].id !== nextWrappers[i].id) {
      return true;
    }
  }

  return false;
}

export function BuildDOMTree(
  layoutLines: LayoutLine[],
  options: ISplitOptions,
  fontMetrics: FontMetrics
): { fragment: DocumentFragment; extraProps: Map<string, string> } {
  const fragment = document.createDocumentFragment();
  const hasLine =
    hasOption(options, "line") || hasOption(options, "charLine") || hasOption(options, "wordLine");
  let localCharIndex = 0;

  const hasChar =
    hasOption(options, "char") || hasOption(options, "charLine") || hasOption(options, "charWord");

  let totalWords = 0;
  layoutLines.forEach((line) => (totalWords += line.words.length));

  let charsCount = 0;
  layoutLines.forEach((line) => line.words.forEach((w) => (charsCount += w.chars.length)));

  const linesCount = layoutLines.length;
  const wordsGlobalCount = totalWords;

  const extraProps = new Map<string, string>();

  layoutLines.forEach((line, li) => {
    const isLastInLayout = li === layoutLines.length - 1;

    let container: HTMLElement | DocumentFragment = fragment;
    let lineText = "";

    if (hasLine) {
      container = document.createElement("span");
      container.setAttribute("aria-hidden", "true");
      container.classList.add("-s-line");
      if (line.isBeforeElement)
        (container as HTMLElement).classList.add(SPLIT_ELEMENT_CLASS.BEFORE_ELEMENT);
      if (line.isAfterElement)
        (container as HTMLElement).classList.add(SPLIT_ELEMENT_CLASS.AFTER_ELEMENT);
      (container as HTMLElement).style.setProperty("--line-index", String(line.lineIndex));
      (container as HTMLElement).style.setProperty("--word-total", String(line.words.length));
      if (line.fitFontSize !== undefined) {
        (container as HTMLElement).style.setProperty(
          "--fit-font-size",
          String(line.fitFontSize),
        );
      }
      applyStyles(container as HTMLElement, (line as any).calculatedValues, options);
    }

    let wrapperStack: { info: WrapperInfo; element: HTMLElement }[] = [];
    let appendTarget: HTMLElement | DocumentFragment = container;

    line.words.forEach((word, wi) => {
      const isLast = wi === line.words.length - 1;

      const targetWrappers = getWordWrappers(word);
      let commonLen = 0;
      while (
        commonLen < wrapperStack.length &&
        commonLen < targetWrappers.length &&
        wrapperStack[commonLen].info.id === targetWrappers[commonLen].id
      ) {
        commonLen++;
      }
      while (wrapperStack.length > commonLen) {
        wrapperStack.pop();
      }
      appendTarget =
        wrapperStack.length > 0
          ? wrapperStack[wrapperStack.length - 1].element
          : container;

      if (!hasLine && (li > 0 || wi > 0)) {
        const brCount = word.explicitBrCountBefore ?? 0;
        for (let b = 0; b < brCount; b++) {
          (appendTarget as HTMLElement).appendChild(document.createElement("br"));
        }
      }

      for (let i = commonLen; i < targetWrappers.length; i++) {
        const wInfo = targetWrappers[i];
        const wEl = document.createElement(wInfo.tag);
        for (const [name, value] of wInfo.attributes) {
          wEl.setAttribute(name, value);
        }
        (appendTarget as HTMLElement).appendChild(wEl);
        wrapperStack.push({ info: wInfo, element: wEl });
        appendTarget = wEl;
      }

      const isElementWord = word.chars.length === 1 && word.chars[0].token.type === "element";
      if (isElementWord) {
        const original = word.chars[0].token.node.cloneNode(true) as HTMLElement;
        (appendTarget as HTMLElement).appendChild(original);
        if (hasLine && isLast && !isLastInLayout) {
          const nextLine = layoutLines[li + 1];
          const brCount = nextLine?.words[0]?.explicitBrCountBefore ?? 1;
          for (let b = 0; b < Math.max(1, brCount); b++) {
            (container as HTMLElement).appendChild(document.createElement("br"));
          }
        }
        return;
      }

      const wordText = word.chars.map((c) => c.char).join("");
      if (wordText) {
        lineText += lineText.length === 0 || word.noSpaceBefore ? wordText : ` ${wordText}`;
      }

      const hasWord =
        hasOption(options, "word") ||
        hasOption(options, "charWord") ||
        hasOption(options, "wordLine");

      const wordEl = hasWord
        ? document.createElement("span")
        : (appendTarget as HTMLElement);

      const wholeWordClass = getWholeWordSplitClass(word);

      if (hasWord) {
        wordEl.setAttribute("aria-hidden", "true");
        wordEl.classList.add("-s-word");
        if (word.isBeforeElement) wordEl.classList.add(SPLIT_ELEMENT_CLASS.BEFORE_ELEMENT);
        if (word.isAfterElement) wordEl.classList.add(SPLIT_ELEMENT_CLASS.AFTER_ELEMENT);
        wordEl.style.setProperty("--word-index", String(word.wordIndexGlobal));
        wordEl.style.setProperty("--char-total", String(word.chars.length));
        wordEl.setAttribute("data-split-content", wordText);
        applyStyles(wordEl, (word as any).calculatedValues, options);

        if (wholeWordClass.length) {
          wordEl.classList.add(...wholeWordClass);
        }
      }

      if (hasChar) {
        word.chars.forEach((char, charIndex) => {
          if (char.char === " " || char.char === "\t") return;

          const charNode = document.createElement("span");
          charNode.setAttribute("aria-hidden", "true");
          const charEl = charNode as HTMLElement;

          charEl.classList.add("-s-char");
          if (char.isBeforeElement) charEl.classList.add(SPLIT_ELEMENT_CLASS.BEFORE_ELEMENT);
          if (char.isAfterElement) charEl.classList.add(SPLIT_ELEMENT_CLASS.AFTER_ELEMENT);

          charEl.textContent = char.char;
          charEl.setAttribute("data-split-content", char.char);
          charEl.style.setProperty("--char-index", String(localCharIndex++));

          const nextChar = word.chars[charIndex + 1];
          if (nextChar) {
            const kerning = fontMetrics.getKerning(char.char, nextChar.char);
            if (Math.abs(kerning) > 0.01) {
              charEl.style.setProperty("--kerning", `${kerning.toFixed(2)}px`);
              charEl.style.marginRight = `var(--kerning)`;
            }
          }

          applyStyles(charEl, (char as any).calculatedValues, options);

          const splitClassChar: string[] = (char as any).splitClass ?? [];
          // Apply class to char when: no word element (hasWord=false, so wholeWordClass
          // has nowhere to go), OR word element doesn't already carry the class.
          if (splitClassChar.length && (!wholeWordClass.length || !hasWord)) {
            charEl.classList.add(...splitClassChar);
          }

          wordEl.appendChild(charNode);
        });
      } else {
        const textNode = document.createTextNode(wordText);
        wordEl.appendChild(textNode);
      }

      if (hasWord) {
        (appendTarget as HTMLElement).appendChild(wordEl);
      }

      const nextWord = line.words[wi + 1];
      const nextNoSpace =
        nextWord?.noSpaceBefore || shouldTrimGeneratedGap(word, nextWord, options);
      if (hasLine) {
        if (!isLast) {
          if (!nextNoSpace) {
            wordEl.appendChild(document.createTextNode("\u00a0"));
          }
        } else if (!isLastInLayout) {
          const nextLine = layoutLines[li + 1];
          const brCount = nextLine?.words[0]?.explicitBrCountBefore ?? 1;
          for (let b = 0; b < Math.max(1, brCount); b++) {
            (container as HTMLElement).appendChild(document.createElement("br"));
          }
        }
      } else {
        if (!isLast && !nextNoSpace) {
          wordEl.appendChild(document.createTextNode("\u00a0"));
        }
      }
    });

    if (hasLine) {
      (container as HTMLElement).setAttribute("data-split-content", lineText);
      fragment.appendChild(container as HTMLElement);
    }
  });

  if (hasLine) {
    extraProps.set("--line-global-total", String(linesCount));
  }

  if (hasChar) {
    extraProps.set("--char-global-total", String(charsCount));
  }
  if (
    hasOption(options, "word") ||
    hasOption(options, "charWord") ||
    hasOption(options, "wordLine")
  ) {
    extraProps.set("--word-global-total", String(wordsGlobalCount));
  }

  return { fragment, extraProps };
}

function applyStyles(
  el: HTMLElement,
  calculatedValues: CalculatedValue[] | undefined,
  options: ISplitOptions
) {
  if (!calculatedValues) return;
  for (const val of calculatedValues) {
    if (!isOptionEnabled(val.type, val.align, options)) continue;
    const varName = generateVariableName(val.type, val.align);
    el.style.setProperty(varName, String(val.value));
  }
}

function isOptionEnabled(type: string, align: string, options: ISplitOptions): boolean {
  const group = options[type as keyof ISplitOptions] ?? [];
  return (
    Array.isArray(group) &&
    group.some((item) =>
      align.startsWith("random") ? item.align.startsWith("random") : item.align === align
    )
  );
}

function generateVariableName(type: string, align: string): string {
  const norm = align.startsWith("random") ? "random" : align;
  return `--${type}-${norm}`;
}

function hasOption(options: ISplitOptions, key: keyof ISplitOptions): boolean {
  return Array.isArray(options[key]) && options[key]!.length > 0;
}
